import { useEffect, useState } from 'react'
import { fetchOps, retryFailedLeads, type OpsResponse } from '../../lib/api'
import { useFlowFilter } from '../FlowFilterContext'

interface Props { range: { from: number; to: number } }

const fmtPct = (p: number) => `${(p * 100).toFixed(1)}%`

function tone(rate: number, good = 0.95, ok = 0.85): 'good' | 'mid' | 'bad' {
  if (rate >= good) return 'good'
  if (rate >= ok) return 'mid'
  return 'bad'
}

export function OpsHealth({ range }: Props) {
  const { variantParam, audienceParam } = useFlowFilter()
  const [data, setData] = useState<OpsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [retrying, setRetrying] = useState(false)
  const [retryMsg, setRetryMsg] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setData(null); setError(null)
    fetchOps(range, variantParam, audienceParam)
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e) => { if (!cancelled) setError(String(e)) })
    return () => { cancelled = true }
  }, [range.from, range.to, variantParam, audienceParam])

  const onRetry = async () => {
    setRetrying(true); setRetryMsg(null)
    try {
      // Pass current filter (range + variant + audience) så retry kun rammer
      // de leads brugeren kan se i Ops-widgetten — ikke alle failed leads
      // i hele DB-historikken (det var den gamle adfærd og var farlig fordi
      // permanente konfig-fejl fra måneder tilbage ville blive re-queued).
      const r = await retryFailedLeads(range, variantParam, audienceParam)
      setRetryMsg(`${r.requeued} leads sendt til retry-køen`)
      // refetch
      const next = await fetchOps(range, variantParam, audienceParam)
      setData(next)
    } catch (e) {
      setRetryMsg(`Fejl: ${String(e)}`)
    } finally {
      setRetrying(false)
    }
  }

  if (error) return <div className="card card-empty">Kunne ikke hente ops-data</div>
  if (!data) return <div className="card card-skeleton" aria-busy />

  const z = data.zapier
  const total = z.sent + z.pending + z.failed

  return (
    <div className="ops-grid">
      <div className="card">
        <div className="card-header">
          <div className="card-title">Zapier delivery</div>
          <div className="card-subtitle">Pr. periode</div>
        </div>
        <div className={`big-stat tone-${tone(z.deliveryRate)}`}>
          {total === 0 ? '—' : fmtPct(z.deliveryRate)}
        </div>
        <div className="ops-breakdown">
          <span><strong>{z.sent}</strong> sendt</span>
          <span><strong>{z.pending}</strong> pending</span>
          <span className={z.failed > 0 ? 'tone-down' : ''}><strong>{z.failed}</strong> failed</span>
        </div>
        {z.failed > 0 && (
          <div className="ops-actions">
            <button className="btn-action" onClick={onRetry} disabled={retrying}>
              {retrying ? 'Sender…' : `Retry ${z.failed} failed`}
            </button>
            {retryMsg && <span className="muted small">{retryMsg}</span>}
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Duplicate rate</div>
          <div className="card-subtitle">Samme nummer inden for 24t</div>
        </div>
        <div className={`big-stat tone-${data.duplicates.rate < 0.05 ? 'good' : data.duplicates.rate < 0.15 ? 'mid' : 'bad'}`}>
          {fmtPct(data.duplicates.rate)}
        </div>
        <div className="muted small">{data.duplicates.count} duplikater registreret</div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Validation rejects</div>
          <div className="card-subtitle">Sidste 24 timer</div>
        </div>
        <div className={`big-stat tone-${data.validationRejects.last24h === 0 ? 'good' : data.validationRejects.last24h < 10 ? 'mid' : 'bad'}`}>
          {data.validationRejects.last24h}
        </div>
        {data.validationRejects.reasons.length > 0 ? (
          <ul className="reject-reasons">
            {data.validationRejects.reasons.map((r) => (
              <li key={r.reason}>
                <span className="muted">{r.reason}</span>
                <span className="num">{r.n}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="muted small">Ingen forsøg på fake numre</div>
        )}
      </div>
    </div>
  )
}
